import React, {FC} from 'react'
import {useTranslation} from 'react-i18next'
import {NavLink, useHistory} from 'react-router-dom'
import {FormModelType, useForm} from 'reactjs-use-form'

import {Box, Link, Stack, TextField} from '@mui/material'
import {Theme, useTheme} from '@mui/material/styles'
import {PGTypography} from '@progynyglobal/pg-ui-library'
import {useSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'
import * as Yup from 'yup'

import blueSideBackground from '@progyny/assets/images/side-banner-white-top.png'
import SubmitButton from '@progyny/components/Buttons/SubmitButton'
import {numericRegex, PasswordField} from '@progyny/components/PasswordField'
import {SidepanelContainer} from '@progyny/components/SidepanelContainer'
import {useSignupMemberMutation} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import {useResponsive} from '@progyny/hooks/useResponsive'
import {RouteEnum, useRoutes} from '@progyny/routes'

export const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    height: '100vh',
  },
  picContainer: {
    background: `${theme.palette.other.brandColors.lightCoral} no-repeat url(${blueSideBackground})`,
    backgroundPosition: 'center 70%',
    width: '50%',
    maxWidth: 500,
    color: theme.palette.other.brandColors.white,
  },
  formContainer: {
    marginTop: '-56px',
    width: 420,
    '& h4': {
      fontWeight: 'normal',
    },
  },
  logo: {
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(4),
  },
  button: {
    margin: `${theme.spacing(4)} 0`,
    display: 'block',
  },
  register: {
    marginBottom: theme.spacing(2.5),
    fontSize: 34,
    fontWeight: 500,
    fontFamily: 'poppins',
  },
}))

const SignUp: FC = () => {
  const {classes} = useStyles()
  const history = useHistory()
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()
  const [signupEmployeeMutation, {loading}] = useSignupMemberMutation()
  const getErrorMessage = useErrorCodes()
  const {getPath} = useRoutes()

  const emailValidation = Yup.string().email().nullable()
  const isDownSM = useResponsive('down', 'sm')
  const theme = useTheme()

  const formModel: FormModelType = {
    email: {
      value: '',
      required: true,
      validator: (email) => {
        if (!emailValidation.isValidSync(email)) {
          return getErrorMessage('invalid-email')
        } else return ''
      },
    },
    password: {
      value: '',
      required: true,
      validator: (password) => {
        if (password.length < 12) {
          return t('profile_security_password_too_short')
        } else if (numericRegex.test(password)) {
          return t('profile_security_password_numeric_only')
        } else return ''
      },
    },
  }

  const {values, errors, isDisabled, handleOnSubmit, handleOnChange} = useForm(
    formModel,
    handleSubmit
  )

  const noAuthLanguage = localStorage.getItem('ui_language') || 'en'

  function handleSubmit() {
    signupEmployeeMutation({
      variables: {
        email: values.email,
        password: values.password,
        language: noAuthLanguage,
      },
    })
      .then((res) => {
        const result = res.data?.signupMember
        // Redirect to confirm email page
        if (result?.errors) {
          // Suppress this error so no phishing can be done.
          if ((result?.errors[0] as string) === 'no-such-email') {
            return history.push(getPath(RouteEnum.CONFIRM_EMAIL), {
              email: values.email,
            })
          }
          const message = getErrorMessage(result?.errors[0] as string)
          return enqueueSnackbar({message, variant: 'error'})
        } else {
          return history.push(getPath(RouteEnum.CONFIRM_EMAIL), {
            email: values.email,
          })
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  return (
    <SidepanelContainer
      styleProps={{
        backgroundPicSrc: blueSideBackground,
        backgroundColor: theme.palette.primary.main,
        logoColor: 'white',
      }}
      mainComponent={
        <Stack gap={4} mb={2} sx={{flexDirection: isDownSM ? 'column' : 'row'}}>
          <Box className={classes.formContainer}>
            <PGTypography className={classes.register} textAlign={'center'}>
              {t('register_complete_register_to_progyny')}
            </PGTypography>

            <Box whiteSpace={'nowrap'} fontSize={'inherit'} mb={4}>
              <PGTypography textAlign={'center'}>
                {t('register_request_email_already_have_account')}{' '}
                <Link component={NavLink} to={getPath(RouteEnum.LOGIN)}>
                  {t('register_request_email_please_login')}
                </Link>
              </PGTypography>
            </Box>

            <form onSubmit={handleOnSubmit}>
              <Box>
                <TextField
                  size='small'
                  variant='outlined'
                  margin='dense'
                  label={t('register_request_email_company_email_address')}
                  placeholder={t('yourname@yourcompany.com')}
                  name='email'
                  value={values.email}
                  onChange={handleOnChange}
                  error={errors?.email.hasError}
                  helperText={t(errors?.email.message)}
                  fullWidth
                  inputProps={{
                    'data-testid': 'email',
                    autoComplete: 'email',
                  }}
                />
              </Box>
              <Box mt={3}>
                <Box mt={1}>
                  <PasswordField
                    focusInfo
                    showStrength
                    label={t('password_field_password')}
                    onChange={handleOnChange}
                    errors={errors}
                    values={{password: values.password}}
                    placeholder={t('register_complete_choose_strong_password')}
                    inputProps={{
                      'data-testid': 'password',
                    }}
                  />
                </Box>
              </Box>
              <SubmitButton
                type='submit'
                loading={loading}
                disabled={isDisabled}
                className={classes.button}
                fullWidth
              >
                {t('routes_nav_sign_up')}
              </SubmitButton>
            </form>
          </Box>
        </Stack>
      }
    />
  )
}

export default SignUp
